/**
 * MatchStatsSystem - Tracks possession, shots on target, fouls, etc.
 * Covers tasks 3.13 (possession %), 3.14 (shots on target), 3.4 (halftime stats),
 * 3.5 (end screen stats), 1.13 (offside tracking).
 */

import * as BABYLON from "babylonjs";

export interface MatchStats {
  playerPossessionTime: number;
  opponentPossessionTime: number;
  playerShotsOnTarget: number;
  opponentShotsOnTarget: number;
  playerShots: number;
  opponentShots: number;
  playerCorners: number;
  opponentCorners: number;
  playerFouls: number;
  opponentFouls: number;
  playerThrowIns: number;
  opponentThrowIns: number;
  offsides: number;
  totalTime: number; // seconds elapsed
}

export class MatchStatsSystem {
  private _stats: MatchStats = this._createEmpty();
  private _lastPossessionHolder: "player" | "opponent" | "none" = "none";
  private _possessionTimer = 0;

  // For offside detection: track last positions of opponents relative to ball
  private _lastPlayerPos = BABYLON.Vector3.Zero();
  private _lastBallPos = BABYLON.Vector3.Zero();

  reset(): void {
    this._stats = this._createEmpty();
    this._lastPossessionHolder = "none";
    this._possessionTimer = 0;
  }

  private _createEmpty(): MatchStats {
    return {
      playerPossessionTime: 0,
      opponentPossessionTime: 0,
      playerShotsOnTarget: 0,
      opponentShotsOnTarget: 0,
      playerShots: 0,
      opponentShots: 0,
      playerCorners: 0,
      opponentCorners: 0,
      playerFouls: 0,
      opponentFouls: 0,
      playerThrowIns: 0,
      opponentThrowIns: 0,
      offsides: 0,
      totalTime: 0,
    };
  }

  // ── Possession tracking ───────────────────────────────────────────

  updatePossession(
    holder: "player" | "opponent" | "none",
    dt: number
  ): void {
    this._stats.totalTime += dt;
    if (holder === "player") {
      this._stats.playerPossessionTime += dt;
    } else if (holder === "opponent") {
      this._stats.opponentPossessionTime += dt;
    }
    this._lastPossessionHolder = holder;
  }

  /** Returns player possession percentage (0–100) */
  getPlayerPossessionPercent(): number {
    const total = this._stats.playerPossessionTime + this._stats.opponentPossessionTime;
    if (total < 0.1) return 50;
    return Math.round((this._stats.playerPossessionTime / total) * 100);
  }

  /** Returns opponent possession percentage (0–100) */
  getOpponentPossessionPercent(): number {
    return 100 - this.getPlayerPossessionPercent();
  }

  // ── Shot tracking ─────────────────────────────────────────────────

  recordShot(side: "player" | "opponent", onTarget: boolean): void {
    if (side === "player") {
      this._stats.playerShots++;
      if (onTarget) this._stats.playerShotsOnTarget++;
    } else {
      this._stats.opponentShots++;
      if (onTarget) this._stats.opponentShotsOnTarget++;
    }
  }

  // ── Set piece tracking ────────────────────────────────────────────

  recordCorner(side: "player" | "opponent"): void {
    if (side === "player") this._stats.playerCorners++;
    else this._stats.opponentCorners++;
  }

  recordThrowIn(side: "player" | "opponent"): void {
    if (side === "player") this._stats.playerThrowIns++;
    else this._stats.opponentThrowIns++;
  }

  recordFoul(side: "player" | "opponent"): void {
    if (side === "player") this._stats.playerFouls++;
    else this._stats.opponentFouls++;
  }

  recordOffside(): void {
    this._stats.offsides++;
  }

  // ── Getters ───────────────────────────────────────────────────────

  getStats(): MatchStats {
    return { ...this._stats };
  }

  getPlayerShotsOnTarget(): number {
    return this._stats.playerShotsOnTarget;
  }

  getOpponentShotsOnTarget(): number {
    return this._stats.opponentShotsOnTarget;
  }

  // ── Offside Detection (1.13) ──────────────────────────────────────

  /**
   * Check if player is offside.
   * In football: player is offside if they are nearer to the opponent's goal
   * than both the ball and the second-to-last opponent (usually the last outfield defender).
   * 
   * Simplified: player is offside if they are past the last defender
   * when the ball is played forward to them.
   * 
   * @param playerZ Player's Z position
   * @param ballZ Ball's Z position
   * @param opponentGoalZ The goal the player is attacking (positive Z = opponent goal)
   * @param defenderPositions Array of opponent defender Z positions
   * @returns true if offside
   */
  static checkOffside(
    playerZ: number,
    ballZ: number,
    opponentGoalZ: number,
    defenderPositions: number[]
  ): boolean {
    // Player must be in opponent's half to be offside
    const inOpponentHalf = opponentGoalZ > 0 ? playerZ > 0 : playerZ < 0;
    if (!inOpponentHalf) return false;

    // Player must be ahead of the ball
    const aheadOfBall = opponentGoalZ > 0
      ? playerZ > ballZ + 0.5
      : playerZ < ballZ - 0.5;
    if (!aheadOfBall) return false;

    // Find second-to-last defender (last defender = goalkeeper)
    const sortedDefenders = [...defenderPositions].sort((a, b) =>
      opponentGoalZ > 0 ? b - a : a - b
    );
    const lastOutfieldDefenderZ = sortedDefenders[1] ?? sortedDefenders[0] ?? opponentGoalZ;

    // Player is past last outfield defender?
    const pastDefender = opponentGoalZ > 0
      ? playerZ > lastOutfieldDefenderZ
      : playerZ < lastOutfieldDefenderZ;

    return pastDefender;
  }
}
